import {ChangeDetectorRef, Directive, ElementRef, OnChanges, OnDestroy, SimpleChanges, Type, forwardRef, inject, output, viewChild, input, linkedSignal, computed} from '@angular/core';
import { Subscription } from 'rxjs';
import { AwsService } from '@app/services/aws.service';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { isHelpPosition, resolveHelpText, SbHelpPosition } from '../sb-field-help';
import { SbDateTimeState } from '../sb-date-time/sb-date-time-control.component';
import { SB_COMPLEX_ROWS } from '../sb-complex-multiple/sb-complex-row.directive';
import {
  boundDay,
  boundMessage,
  boundNotice,
  invalidTextMessage,
  outOfBounds,
  SbDateTimeMode,
  SbDateTimeValueFormat,
  SbDateValueAs,
} from '../sb-date-time/sb-date-time-format';
import { SbQuickPickSet } from '../sb-date-time/sb-quick-picks';

export type { SbHelpPosition } from '../sb-field-help';

export type SbInputType =
  | 'text'
  | 'email'
  | 'ip'
  | 'url'
  | 'color'
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'file'
  | 'image'
  | 'video'
  | 'font'
  | 'password';

export type SbInputTextType = 'text';
export type SbInputFileType = 'file' | 'image' | 'video';
export type SbInputTimeType = 'date' | 'datetime' | 'time';
export type SbInputNumberType = 'number';
export type SbInputPasswordType = 'password';

/** Shared CVA + validator providers for typed sb-input subclasses. */
export function sbInputProviders(component: () => Type<unknown>) {
  return [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(component),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(component),
      multi: true,
    },
  ];
}

const TIME_TYPES: ReadonlySet<SbInputType> = new Set(['date', 'datetime', 'time']);

const MULTIPLE_TYPES: ReadonlySet<SbInputType> = new Set([
  'text',
  'ip',
  'email',
  'font',
  'file',
  'image',
  'video',
  'color',
  'number',
]);

const DEFAULT_IMAGE_FORMATS = 'png,jpg,jpeg,webp,gif';
const DEFAULT_VIDEO_FORMATS = 'mp4,webm,mov,ogg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;

const IPV6_RE = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|(([0-9a-fA-F]{1,4}:){1,7}:)|(([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4})|(([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2})|(([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3})|(([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4})|(([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5})|([0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6}))|(:((:[0-9a-fA-F]{1,4}){1,7}|:)))$/;

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const TIME_RE = /^(?:[01]?\d|2[0-3]):[0-5]\d(?:\s?[AaPp][Mm])?$/;
/** Host must look like a domain (has a dot) or be localhost / an IP. */

const URL_HOST_RE = /^(localhost|(\d{1,3}\.){3}\d{1,3}|([a-z0-9-]+\.)+[a-z]{2,})$/i;
let nextId = 0;

/**
 * Shared implementation for all `app-sb-input-*` controls.
 * Subclasses provide the selector + CVA providers.
 */
@Directive()
export class SbInputBase implements ControlValueAccessor, Validator, OnChanges, OnDestroy {
  /** The type a parent binds; a control made for one type narrows or ignores it (`resolveType`). */
  readonly typeInput = input<SbInputType | SbInputTimeType | null | undefined>(undefined, {alias: 'type'});
  private readonly resolvedType = computed(() => this.resolveType(this.typeInput()));
  get type(): SbInputType {
    return this.resolvedType();
  }
  /** The type this control shows for the bound one: the bound type, text when there is none. */
  protected resolveType(value: SbInputType | SbInputTimeType | null | undefined): SbInputType {
    return (value || 'text') as SbInputType;
  }
  readonly label = input('');
  /** The text box's accessible name where it shows no label. */
  readonly ariaLabel = input('');
  readonly description = input('');
  /** @deprecated Prefer `help`. Still used as fallback when `help` is empty. */
  readonly info = input('');
  /** Help popover text. When set, shows a help icon per `helpPosition`. */
  readonly help = input('');
  /** Where to place the help icon when `help` (or legacy `info`) has a value. */
  readonly helpPosition = input<SbHelpPosition>('afterLabel');
  /** Real placeholder hint. Empty uses floating-label spacer (` `). */
  readonly placeholder = input('');
  readonly required = input(false);
  readonly disabledInput = input(false, {alias: 'disabled'});
  readonly disabled = linkedSignal(() => this.disabledInput());
  readonly readonly = input(false);
  readonly error = input('');
  readonly id = input(`sb-input-${++nextId}`);
  /**
   * The native `autocomplete` attribute of the input, e.g. `new-password` so a browser does not
   * fill a stored login into a credential field. Empty (default) leaves the attribute off.
   * Not to be confused with `app-sb-input-select`'s boolean `autocomplete`, which turns type-ahead
   * filtering of its list on or off.
   */
  readonly autocomplete = input('');
  /**
   * Allowed file formats for `file` / `image` / `video`.
   * Comma-separated extensions (with or without dots), e.g. `png,jpg,webp` or `.pdf,.doc`.
   * Also accepts mime tokens like `image/*` / `video/*`.
   */
  readonly accept = input('');
  /** Max pixel width for `image` / `video` uploads (optional). Also drives preview aspect ratio with `maxHeight`. */
  readonly maxWidth = input<number | string | null>(null);
  /** Max pixel height for `image` / `video` uploads (optional). Also drives preview aspect ratio with `maxWidth`. */
  readonly maxHeight = input<number | string | null>(null);
  /** Min pixel width for `image` / `video` uploads (optional). */
  readonly minWidth = input<number | string | null>(null);
  /** Min pixel height for `image` / `video` uploads (optional). */
  readonly minHeight = input<number | string | null>(null);
  /** Max video duration in seconds (`video` only). */
  readonly maxDuration = input<number | string | null>(null);
  /** Min video duration in seconds (`video` only). */
  readonly minDuration = input<number | string | null>(null);
  /** Max file size in bytes for `file` / `image` / `video`. */
  readonly maxFileSize = input<number | string | null>(null);
  /** Number bound, or for a date field the earliest day: a `Date` (its local day) or `YYYY-MM-DD` / `MM/DD/YYYY` text. */
  readonly min = input<number | string | Date | null>(null);
  readonly max = input<number | string | Date | null>(null);
  /** Number step. */
  readonly step = input<number | string | null>(null);
  /** Date, time and datetime: the one-click picks in the popover. */
  readonly quickPicks = input<SbQuickPickSet>('future');
  /** Date, time and datetime: the IANA zone "today" and "now" are taken in; null is the browser's. Values are never converted. */
  readonly timeZone = input<string | null>(null);
  /** Time type: the page joins this time to a date field, so a quick pick in a repeated hour would save the first. */
  readonly joinsDate = input(false);
  /**
   * Date only: `text` writes `YYYY-MM-DD`; `date` writes a `Date` (a picked day at the current time
   * of day, a typed one at midnight, null when empty) and validates `min`/`max`, as Material's
   * datepicker input did.
   */
  readonly valueAs = input<SbDateValueAs>('text');
  /** Datetime: the text a whole date and time is written in, `YYYY-MM-DDTHH:mm` or `YYYY-MM-DD hh:mm A`. */
  readonly valueFormat = input<SbDateTimeValueFormat>('YYYY-MM-DDTHH:mm');
  /**
   * Time and datetime: a held value the field cannot read (free text saved before the field was a
   * picker) is shown as it is and stays valid, so it saves back unchanged until the field is edited.
   */
  readonly keepUnreadable = input(false);
  /** Time only: a quick pick landed on another day than today (`YYYY-MM-DD`), for a page with a separate date field. */
  readonly dayPicked = output<string>();
  /**
   * Date and time types: an entry is done, focus having left the field and its panel with another
   * value. For a page rule that must not act on a date typing passes through.
   */
  readonly entryDone = output<unknown>();
  /**
   * Show file browser. Defaults to `true` for `image` / `video`, `false` otherwise.
   * Pass `[showFileBrowser]="false"` to hide it on media fields.
   */
  readonly showFileBrowserInput = input<boolean | string | null | undefined>(undefined, {alias: 'showFileBrowser'});
  get showFileBrowser(): boolean {
    const value = this.showFileBrowserInput();
    if (value === undefined || value === null || value === '') {
      return this.isMediaType;
    }
    return value === true || value === 'true';
  }
  readonly showVariableBrowser = input(false);
  /**
   * For `text`: a Condition button beside the variable button that writes `{% if %}` conditional
   * text, and a line under the field naming its conditions or what is wrong with them. Only for
   * fields the server renders with text conditions.
   */
  readonly showConditionBuilder = input(false);
  readonly showFontBrowser = input(false);
  /**
   * @deprecated No-op. The upload/replace control is always present for `file` / `image` / `video`;
   * `showFileBrowser` decides whether it opens the file browser or the native picker.
   */
  readonly showInlineUpload = input(false);
  /**
   * For `file` / `image` / `video`: upload the picked file immediately via `POST /api/v1/file` and
   * set the control value to the stored URL. When false the value is the picked file's name and the
   * host is responsible for uploading.
   */
  readonly autoUpload = input(false);
  /**
   * For `image` / `video`: what clicking the preview (or empty placeholder) opens.
   * `upload` (default) opens the native OS file picker, `browse` opens the app
   * file browser (falls back to upload when `showFileBrowser` is off), `none` disables it.
   */
  readonly imageClickAction = input<'upload' | 'browse' | 'none'>('upload');
  /** For `image` / `video`: show the Remove action when a value is set. */
  readonly allowRemove = input(true);
  /** For `color`: enable the colour picker's alpha / transparency. */
  readonly colorAlpha = input(false);
  readonly clientId = input<number | null>(null);
  readonly projectId = input<number | null>(null);
  readonly variableClickFor = input('');
  readonly variableFieldType = input('text');
  readonly variableFormat = input('plain_text');
  /**
   * Multi-value chip mode. Only for text, ip, email, font, file, image, video, color, number.
   * When true, `delimiter` is required (e.g. `,`).
   */
  readonly multiple = input(false);
  /** Separator used to join/split values when `multiple` is true. */
  readonly delimiter = input(',');
  readonly fileBrowserClick = output<void>();
  readonly variableBrowserClick = output<void>();
  readonly fontBrowserClick = output<void>();
  readonly fileSelected = output<string | FileList | null>();
  /** Emits when the media value is removed via the Remove/clear actions. */
  readonly removed = output<void>();
  /** Emits the stored URL after a successful `autoUpload`. */
  readonly uploaded = output<string>();
  readonly variableSelected = output<unknown>();
  readonly chipInput = viewChild<ElementRef<HTMLInputElement>>('chipInput');
  readonly inlineFileInput = viewChild<ElementRef<HTMLInputElement>>('inlineFileInput');
  readonly mediaFileBrowser = viewChild<{
    openFileBrowser: () => void;
  }>('mediaFileBrowser');
  value = '';
  chips: string[] = [];
  draft = '';
  /** What a date, time or datetime field holds: its text contract, or a `Date` with `valueAs="date"`. */
  timeValue: unknown = '';
  /** Bumped on every `writeValue`, so the field resets its text even for a value written twice. */
  timeRevision = 0;
  /** The field's text does not make a value yet (a half-typed date), while the value is `''`. */
  private timeTextInvalid = false;
  private timeText = '';
  /** The field's message about its text (typed or pasted). */
  private timeError = '';
  /** The field's own muted line ("Year first: type the year, then - or /"). */
  private timeLine = '';
  /** `keepUnreadable`: the value is the one written from outside, not yet edited in the field. */
  private timeHeld = false;
  /** The date or time errors the control last read from `validate`. */
  private timeErrors = 'null';
  internalError = '';
  /** Object URL for local image preview (revoked on change). */
  private localPreviewUrl = '';
  private mediaValidateSeq = 0;
  /** 0-100 while an autoUpload is in flight, null otherwise. */
  uploadProgress: number | null = null;
  private uploadSub?: Subscription;
  private readonly awsService = inject(AwsService);
  private readonly cdr = inject(ChangeDetectorRef);
  /** The rows this field sits in, if any. */
  private readonly complexRows = inject(SB_COMPLEX_ROWS, {optional: true});
  /** After blur / interact — required + format messages surface under the field. */
  touched = false;
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;
  get htmlType(): string {
    if (this.type === 'font' || this.type === 'ip' || this.type === 'url') {
      return 'text';
    }
    return this.type;
  }
  get supportsMultiple(): boolean {
    return MULTIPLE_TYPES.has(this.type);
  }
  get isMultiple(): boolean {
    return this.multiple() && this.supportsMultiple;
  }
  get isMediaType(): boolean {
    return this.type === 'image' || this.type === 'video';
  }
  /** True while an autoUpload is in flight. */
  get isUploading(): boolean {
    return this.uploadProgress !== null;
  }
  /**
   * Human-readable summary of the active upload restrictions, one per line. Drives the info icon so
   * limits are disclosed up front instead of only when a pick trips them.
   */
  get restrictionText(): string {
    if (this.type !== 'file' && !this.isMediaType) {
      return '';
    }
    const lines: string[] = [];

    const accept = this.resolvedAccept;
    if (accept) {
      const formats = accept
        .split(',')
        .map((token) => token.trim())
        .filter(Boolean)
        .map((token) => (token.startsWith('.') ? token.slice(1).toUpperCase() : token));
      if (formats.length) {
        lines.push(`Formats: ${formats.join(', ')}`);
      }
    }

    const maxBytes = this.toPositiveNumber(this.maxFileSize());
    if (maxBytes) {
      lines.push(`Max size: ${this.formatBytes(maxBytes)}`);
    }

    if (this.isMediaType) {
      const dimensions = this.describeDimensions();
      if (dimensions) {
        lines.push(dimensions);
      }
    }

    if (this.type === 'video') {
      const minDur = this.toPositiveNumber(this.minDuration());
      const maxDur = this.toPositiveNumber(this.maxDuration());
      if (minDur && maxDur) {
        lines.push(`Duration: ${minDur}–${maxDur}s`);
      } else if (maxDur) {
        lines.push(`Max duration: ${maxDur}s`);
      } else if (minDur) {
        lines.push(`Min duration: ${minDur}s`);
      }
    }

    return lines.join('\n');
  }
  /** True when there is at least one restriction worth disclosing. */
  get hasRestrictions(): boolean {
    return !!this.restrictionText;
  }
  /** Author-supplied help first, then the generated restriction summary. */
  get helpText(): string {
    const authored = resolveHelpText(this.help(), this.info());
    const restrictions = this.restrictionText;
    if (authored && restrictions) {
      return `${authored}\n\n${restrictions}`;
    }
    return authored || restrictions;
  }
  /** `Dimensions: 800 × 800 to 2048 × 2048 px`, degrading to whichever bounds are set. */
  private describeDimensions(): string {
    const minW = this.toPositiveNumber(this.minWidth());
    const minH = this.toPositiveNumber(this.minHeight());
    const maxW = this.toPositiveNumber(this.maxWidth());
    const maxH = this.toPositiveNumber(this.maxHeight());
    const pair = (w: number | null, h: number | null): string => {
      if (w && h) {
        return `${w} × ${h}`;
      }
      if (w) {
        return `${w} wide`;
      }
      return h ? `${h} high` : '';
    };
    const min = pair(minW, minH);
    const max = pair(maxW, maxH);
    if (min && max) {
      return `Dimensions: ${min} to ${max} px`;
    }
    if (max) {
      return `Max dimensions: ${max} px`;
    }
    return min ? `Min dimensions: ${min} px` : '';
  }
  get showHelpAfterLabel(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'afterLabel');
  }
  get showHelpEndInside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndInside');
  }
  get showHelpEndOutside(): boolean {
    return isHelpPosition(this.helpText, this.helpPosition(), 'inputEndOutside');
  }
  /**
   * `file` / `image` / `video` disclose their help alongside the action controls — at the bottom of
   * the media field, or in the trailing icon row of a file field — instead of beside the label.
   * An explicit `inputEndInside` / `inputEndOutside` still wins.
   */
  get showHelpInActions(): boolean {
    if (!this.isMediaType && this.type !== 'file') {
      return false;
    }
    return !!this.helpText && !this.showHelpEndInside && !this.showHelpEndOutside;
  }
  get hasCompanions(): boolean {
    // Media types host browser/upload under the preview, and `file` hosts them in its trailing icon
    // row — neither uses the absolutely-positioned field-end companions.
    if (this.isMediaType || this.type === 'file') {
      return !this.isMultiple && (
        this.showHelpEndInside
        || this.showVariableBrowser()
        || this.showFontBrowser()
      );
    }
    return !this.isMultiple && (
      this.showFileBrowser
      || this.showVariableBrowser()
      || this.hasConditionBuilder
      || this.showFontBrowser()
      || this.type === 'font'
      || this.showHelpEndInside
    );
  }
  get hasConditionBuilder(): boolean {
    return this.showConditionBuilder() && this.type === 'text' && !this.isMultiple;
  }
  get inputPlaceholder(): string {
    return this.placeholder() || ' ';
  }
  get useFloatingPlaceholder(): boolean {
    return !!this.placeholder();
  }
  get resolvedVariableFor(): string {
    return this.variableClickFor() || this.id();
  }
  /** Normalized accept string for native file inputs + validation. */
  get resolvedAccept(): string {
    const raw = (this.accept() || '').trim();
    if (raw) {
      return this.normalizeAcceptList(raw);
    }
    if (this.type === 'image') {
      return this.normalizeAcceptList(DEFAULT_IMAGE_FORMATS);
    }
    if (this.type === 'video') {
      return this.normalizeAcceptList(DEFAULT_VIDEO_FORMATS);
    }
    return '';
  }
  /** Passed to app-file-browser `type` filter. */
  get fileBrowserType(): string {
    if (this.type === 'image') {
      return 'images';
    }
    if (this.type === 'video') {
      return 'video';
    }
    return '';
  }
  get mediaPreviewUrl(): string {
    if (!this.isMediaType) {
      return '';
    }
    if (this.localPreviewUrl) {
      return this.localPreviewUrl;
    }
    const v = String(this.value || '').trim();
    if (!v) {
      return '';
    }
    if (/^(https?:|data:|blob:|\/)/i.test(v)) {
      return v;
    }
    if (this.type === 'image' && /\.(png|jpe?g|gif|webp|svg|bmp)(\?|#|$)/i.test(v)) {
      return v;
    }
    if (this.type === 'video' && /\.(mp4|webm|ogg|mov)(\?|#|$)/i.test(v)) {
      return v;
    }
    return '';
  }
  /** @deprecated use mediaPreviewUrl */
  get imagePreviewUrl(): string {
    return this.type === 'image' ? this.mediaPreviewUrl : '';
  }
  /**
   * CSS aspect-ratio for media preview.
   * Uses maxWidth/maxHeight when both are set; otherwise square (1 / 1).
   */
  get mediaAspectRatio(): string {
    const w = this.toPositiveNumber(this.maxWidth());
    const h = this.toPositiveNumber(this.maxHeight());
    if (w && h) {
      return `${w} / ${h}`;
    }
    return '1 / 1';
  }
  get isTimeType(): boolean {
    return TIME_TYPES.has(this.type);
  }
  get timeMode(): SbDateTimeMode {
    return this.type as SbDateTimeMode;
  }
  /** The description and error ids a date or time input lists in `aria-describedby`. */
  get timeDescribedBy(): string {
    return [this.description() ? `${this.id()}-description` : '', this.displayError ? `${this.id()}-error` : '']
      .filter(Boolean)
      .join(' ');
  }
  /**
   * Text-mode date and datetime fields keep a day outside `min`..`max` as typed, as before, and
   * say so under the field without an error.
   */
  get timeNotice(): string {
    if (this.isTimeType && this.timeLine && !this.displayError) {
      return this.timeLine;
    }
    if (!this.isTimeType || this.type === 'time' || this.valueAs() === 'date' || this.displayError) {
      return '';
    }
    const day = boundDay(String(this.timeValue ?? '').slice(0, 10));
    const min = boundDay(this.min());
    const max = boundDay(this.max());
    const side = day ? outOfBounds(day, min, max) : null;
    const bound = side === 'min' ? min : max;
    return side && bound ? boundNotice(side, bound) : '';
  }
  get displayError(): string {
    const error = this.error();
    if (error) {
      return error;
    }
    if (this.isTimeType) {
      if (this.timeHeld && !this.isTimeEmpty()) {
        // A held value is not checked again; a paste refused over it still says why.
        return this.timeError;
      }
      const message = this.timeError || (this.timeTextInvalid ? '' : this.timeValueError());
      // An entry being typed is not an empty field: it says nothing until it is left or committed.
      return message || (this.touched && this.required() && this.isTimeEmpty() && !this.timeTextInvalid ? 'This field is required' : '');
    }
    if (this.internalError) {
      return this.internalError;
    }
    if (this.touched && this.required()) {
      if (this.isMultiple && !this.chips.length) {
        return 'This field is required';
      }
      if (!this.isMultiple && !String(this.value || '').trim()) {
        return 'This field is required';
      }
    }
    return '';
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.multiple
      || changes.typeInput
      || changes.delimiter
      || changes.accept
      || changes.colorAlpha
      || changes.maxWidth
      || changes.maxHeight
      || changes.minWidth
      || changes.minHeight
      || changes.maxDuration
      || changes.minDuration
      || changes.maxFileSize
      || changes.min
      || changes.max
      || changes.valueAs
    ) {
      const multiple = this.multiple();
      if (multiple && !this.supportsMultiple) {
        this.internalError = `Multiple is not supported for type "${this.type}"`;
      } else if (multiple && !this.delimiter()) {
        this.internalError = 'Delimiter is required when multiple is enabled';
      } else if (!this.error()) {
        this.revalidateCurrent();
      }
      if (this.isTimeType) {
        this.revalidateTime();
      } else {
        this.onValidatorChange();
      }
    }
  }

  ngOnDestroy(): void {
    this.uploadSub?.unsubscribe();
    this.clearLocalPreview();
    this.complexRows?.rowEntryGone(this);
  }

  /** The forms API calls this and `setDisabledState` from outside the template (a `patchValue` in a subscribe). */
  writeValue(value: string | string[] | null): void {
    if (Array.isArray(value)) {
      this.chips = value.map((v) => String(v).trim()).filter(Boolean);
      this.value = this.chips.join(this.delimiter() || ',');
    } else if (this.isTimeType) {
      this.timeValue = value ?? (this.valueAs() === 'date' ? null : '');
      this.value = value === null || value === undefined ? '' : String(value);
      this.timeRevision += 1;
      this.timeTextInvalid = false;
      this.complexRows?.rowEntryChanged(this, false);
      this.timeError = '';
      this.timeHeld = this.keepUnreadable();
    } else {
      this.value = value ?? '';
      this.chips = this.isMultiple ? this.splitValue(this.value) : [];
    }
    this.clearLocalPreview();
    this.revalidateCurrent();
    this.cdr.markForCheck();
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.cdr.markForCheck();
  }
  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.isTimeType) {
      const errors = this.validateTime();
      this.timeErrors = JSON.stringify(errors);
      return errors;
    }
    const multiple = this.multiple();
    if (multiple && !this.supportsMultiple) {
      return { sbInputMultipleUnsupported: true };
    }
    if (multiple && !this.delimiter()) {
      return { sbInputDelimiterRequired: true };
    }
    if (this.required()) {
      if (this.isMultiple && !this.chips.length) {
        return { required: true };
      }
      if (!this.isMultiple && !String(this.value || '').trim()) {
        return { required: true };
      }
    }
    if (this.isMultiple) {
      const invalid = this.chips.find((chip) => !this.isValidValue(chip));
      if (invalid) {
        return { sbInputInvalid: { value: invalid, type: this.type } };
      }
      return null;
    }
    if (!String(this.value || '').trim()) {
      return null;
    }
    if (!this.isValidValue(String(this.value).trim())) {
      return { sbInputInvalid: { value: this.value, type: this.type } };
    }
    return null;
  }
  /** A date or time field wrote a value: what was typed or picked, in its contract. */
  onTimeValueChange(value: unknown): void {
    this.timeHeld = false;
    this.timeValue = value;
    this.value = value === null || value === undefined ? '' : String(value);
    this.onChange(value as string);
    this.onValidatorChange();
  }
  /** A date or time field's text changed: whether it makes a value, and its messages. */
  onTimeStateChange(state: SbDateTimeState): void {
    // An edit typed back to the text a held value showed, or kept text brought back with Esc or
    // Ctrl+Z, is that value again, as it was saved.
    this.timeHeld = state.held;
    this.timeTextInvalid = state.textInvalid;
    this.complexRows?.rowEntryChanged(this, state.textInvalid);
    this.timeText = state.text;
    this.timeError = state.error;
    this.timeLine = state.notice;
    this.revalidateTime();
    this.cdr.markForCheck();
  }
  triggerInlineUpload(): void {
    if (this.disabled() || this.readonly()) {
      return;
    }
    this.inlineFileInput()?.nativeElement?.click();
  }
  /** Open the app file browser (media fields). */
  openBrowse(): void {
    if (this.disabled() || this.readonly()) {
      return;
    }
    this.mediaFileBrowser()?.openFileBrowser();
  }
  /** Whether the single pick button is wired to the file browser rather than the native picker. */
  get uploadOpensBrowser(): boolean {
    return this.showFileBrowser;
  }
  /** Icon for the single pick button — upload when empty, replace when a value is already set. */
  get uploadIcon(): string {
    return this.hasMediaValue ? 'fa fa-exchange' : 'fa fa-upload';
  }
  /** Label for the single pick button. Shows progress while an autoUpload is running. */
  get uploadLabel(): string {
    if (this.isUploading) {
      return `${this.uploadProgress}%`;
    }
    return this.hasMediaValue ? 'Replace' : 'Upload';
  }
  /** True when the control holds something to replace or delete. */
  get hasMediaValue(): boolean {
    return this.isMediaType ? !!this.mediaPreviewUrl : !!String(this.value || '').trim();
  }
  /** The single pick action: file browser or native picker, per `showFileBrowser`. */
  startUpload(): void {
    if (this.disabled() || this.readonly() || this.isUploading) {
      return;
    }
    if (this.uploadOpensBrowser && this.mediaFileBrowser()) {
      this.openBrowse();
      return;
    }
    this.triggerInlineUpload();
  }
  /** Whether the media preview / placeholder reacts to clicks. */
  get mediaClickEnabled(): boolean {
    return this.imageClickAction() !== 'none' && !this.disabled() && !this.readonly() && !this.isUploading;
  }
  /** Preview / placeholder click runs the same single pick action as the button. */
  onMediaPreviewClick(): void {
    if (!this.mediaClickEnabled) {
      return;
    }
    this.startUpload();
  }
  /** Empty media placeholder: same configurable action as the preview. */
  onMediaPlaceholderClick(): void {
    this.onMediaPreviewClick();
  }
  /**
   * Clears the control locally only — never calls a delete endpoint. The previously stored file is
   * reaped server-side when the owning entity is saved, so an unsaved delete stays cancellable.
   */
  clearMedia(): void {
    if (this.disabled() || this.readonly() || this.isUploading) {
      return;
    }
    this.mediaValidateSeq += 1;
    this.clearLocalPreview();
    this.internalError = '';
    this.setSingleValue('');
    this.fileSelected.emit('');
    this.removed.emit();
    const inlineFileInput = this.inlineFileInput();
    if (inlineFileInput?.nativeElement) {
      inlineFileInput.nativeElement.value = '';
    }
  }
  async onInlineFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    this.touched = true;
    if (!this.isAcceptedFile(file)) {
      this.internalError = `File type not allowed. Allowed formats: ${this.resolvedAccept || 'any'}`;
      this.onValidatorChange();
      input.value = '';
      return;
    }
    const sizeError = this.validateFileSize(file.size);
    if (sizeError) {
      this.internalError = sizeError;
      this.onValidatorChange();
      input.value = '';
      return;
    }
    if (this.isMediaType) {
      const mediaError = await this.validateMediaFile(file);
      // Past the await, the change event that started this has been handled.
      this.cdr.markForCheck();
      if (mediaError) {
        this.internalError = mediaError;
        this.onValidatorChange();
        input.value = '';
        return;
      }
      this.clearLocalPreview();
      this.localPreviewUrl = URL.createObjectURL(file);
    }
    this.internalError = '';
    this.fileSelected.emit(input.files);
    if (this.autoUpload()) {
      this.uploadPickedFile(file);
    } else {
      // Store path/name as string value (callers can replace with uploaded URL via writeValue).
      this.setSingleValue(file.name);
    }
    input.value = '';
  }
  /**
   * Upload the picked file straight away and set the control value to the stored URL. Uploading on
   * pick (rather than on form submit) also avoids the signed-URL flow's 20-minute expiry window.
   */
  private uploadPickedFile(file: File): void {
    this.uploadSub?.unsubscribe();
    this.uploadProgress = 0;
    this.uploadSub = this.awsService
      .uploadFileWithProgress(file, this.projectId(), this.clientId())
      .subscribe({
        next: (event) => {
          this.cdr.markForCheck();
          if (event.url) {
            this.uploadProgress = null;
            this.clearLocalPreview();
            this.setSingleValue(event.url);
            this.uploaded.emit(event.url);
            return;
          }
          this.uploadProgress = event.progress;
        },
        error: () => {
          this.cdr.markForCheck();
          this.uploadProgress = null;
          this.clearLocalPreview();
          this.setSingleValue('');
          this.internalError = 'Upload failed. Please try again.';
          this.onValidatorChange();
        },
      });
  }
  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.setSingleValue(target.value);
  }
  onBlur(): void {
    this.touched = true;
    this.onTouched();
    this.revalidateCurrent();
    this.onValidatorChange();
  }
  onDraftKeydown(event: KeyboardEvent): void {
    if (!this.isMultiple) {
      return;
    }
    const key = event.key;
    const delimiter = this.delimiter();
    if (key === 'Enter' || key === delimiter || (delimiter === ',' && key === ',')) {
      event.preventDefault();
      this.commitDraft();
      return;
    }
    if (key === 'Backspace' && !this.draft && this.chips.length) {
      this.removeChip(this.chips.length - 1);
    }
  }
  onDraftBlur(): void {
    this.commitDraft();
    this.onBlur();
  }
  commitDraft(): void {
    const raw = (this.draft || '').trim();
    if (!raw) {
      this.draft = '';
      return;
    }
    const delimiter = this.delimiter();
    const parts = delimiter
      ? raw.split(delimiter).map((p) => p.trim()).filter(Boolean)
      : [raw];
    for (const part of parts) {
      if (!this.isValidValue(part)) {
        this.internalError = this.invalidMessage(part);
        return;
      }
      if (!this.chips.includes(part)) {
        this.chips.push(part);
      }
    }
    this.draft = '';
    this.internalError = '';
    this.emitChips();
  }
  removeChip(index: number): void {
    this.chips.splice(index, 1);
    this.internalError = '';
    this.emitChips();
    this.onTouched();
  }
  focusChipInput(): void {
    this.chipInput()?.nativeElement?.focus();
  }
  async onFileBrowserSelected(url: string): Promise<void> {
    this.fileBrowserClick.emit();
    this.touched = true;
    if (this.isMultiple) {
      if (url && this.isValidValue(url) && !this.chips.includes(url)) {
        this.chips.push(url);
        this.emitChips();
      } else if (url && !this.isValidValue(url)) {
        this.internalError = this.invalidMessage(url);
      }
      this.fileSelected.emit(url);
      this.onTouched();
      return;
    }
    if (url && this.isMediaType && this.hasMediaConstraints()) {
      const mediaError = await this.validateMediaUrl(url);
      this.cdr.markForCheck();
      if (mediaError) {
        this.internalError = mediaError;
        this.onValidatorChange();
        this.onTouched();
        return;
      }
    }
    this.clearLocalPreview();
    this.internalError = '';
    this.setSingleValue(url || '');
    this.fileSelected.emit(url);
    this.onTouched();
  }
  onVariablePicked(variable: unknown): void {
    this.variableBrowserClick.emit();
    const text = this.extractVariableText(variable);
    if (this.isMultiple) {
      if (text && this.isValidValue(text) && !this.chips.includes(text)) {
        this.chips.push(text);
        this.emitChips();
      }
    } else {
      this.setSingleValue(`${this.value || ''}${text}`);
    }
    this.variableSelected.emit(variable);
    this.onTouched();
  }
  onFontBrowserSelected(selection: { fontFamily: string }): void {
    this.fontBrowserClick.emit();
    const font = selection?.fontFamily || '';
    if (!font) {
      return;
    }
    if (this.isMultiple) {
      if (this.isValidValue(font) && !this.chips.includes(font)) {
        this.chips.push(font);
        this.emitChips();
      }
    } else {
      this.setSingleValue(font);
    }
    this.onTouched();
  }
  isValidValue(raw: string): boolean {
    const value = (raw || '').trim();
    if (!value) {
      return false;
    }
    switch (this.type) {
      case 'email':
        return EMAIL_RE.test(value);
      case 'ip':
        return IPV4_RE.test(value) || IPV6_RE.test(value);
      case 'url':
        return this.isValidUrl(value);
      case 'number':
        return !Number.isNaN(Number(value)) && Number.isFinite(Number(value));
      case 'color':
        return HEX_COLOR_RE.test(value) || /^rgba?\(/i.test(value);
      case 'time':
        return TIME_RE.test(value);
      case 'date':
        return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
      case 'datetime':
        return this.isValidDateTime(value);
      case 'file':
      case 'image':
      case 'video':
        return this.isValidPathOrUrl(value);
      case 'font':
      case 'text':
      case 'password':
        return value.length > 0;
      default:
        return value.length > 0;
    }
  }
  private invalidMessage(value: string): string {
    switch (this.type) {
      case 'email':
        return `Invalid email: ${value}`;
      case 'ip':
        return `Invalid IP address: ${value}`;
      case 'url':
        return `Invalid URL: ${value}`;
      case 'number':
        return `Invalid number: ${value}`;
      case 'color':
        return `Invalid color: ${value}`;
      case 'file':
      case 'image':
      case 'video':
        return `Invalid ${this.type} path/URL: ${value}`;
      case 'date':
      case 'datetime':
      case 'time':
        return `Invalid ${this.type}: ${value}`;
      default:
        return `Invalid value: ${value}`;
    }
  }
  private isValidUrl(value: string): boolean {
    try {
      const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
      const parsed = new URL(withProtocol);
      if (!['http:', 'https:', 'ftp:'].includes(parsed.protocol)) {
        return false;
      }
      const host = parsed.hostname;
      if (!host || !URL_HOST_RE.test(host)) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }
  private isValidDateTime(value: string): boolean {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      return !Number.isNaN(Date.parse(value));
    }
    // Allow "YYYY-MM-DD h:mm AM" combined forms while editing.
    const [d, ...rest] = value.split(/\s+/);
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      return false;
    }
    const t = rest.join(' ');
    return !t || TIME_RE.test(t);
  }
  private isValidPathOrUrl(value: string): boolean {
    if (this.resolvedAccept && !this.extensionMatchesAccept(value)) {
      return false;
    }
    return value.length > 0;
  }
  private isAcceptedFile(file: File): boolean {
    const accept = this.resolvedAccept;
    if (!accept) {
      return true;
    }
    return this.fileMatchesAccept(file, accept);
  }
  private toPositiveNumber(raw: number | string | null | undefined): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      return null;
    }
    return n;
  }
  private formatBytes(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    const mb = bytes / (1024 * 1024);
    return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
  }
  private validateFileSize(sizeBytes: number): string | null {
    const maxBytes = this.toPositiveNumber(this.maxFileSize());
    if (!maxBytes || !Number.isFinite(sizeBytes)) {
      return null;
    }
    if (sizeBytes > maxBytes) {
      return `File is too large. Max size is ${this.formatBytes(maxBytes)}`;
    }
    return null;
  }
  private hasMediaConstraints(): boolean {
    return !!(
      this.toPositiveNumber(this.maxWidth())
      || this.toPositiveNumber(this.maxHeight())
      || this.toPositiveNumber(this.maxDuration())
      || this.toPositiveNumber(this.minDuration())
      || this.toPositiveNumber(this.maxFileSize())
    );
  }
  private validateMediaDimensions(width: number, height: number): string | null {
    const maxW = this.toPositiveNumber(this.maxWidth());
    const maxH = this.toPositiveNumber(this.maxHeight());
    const minW = this.toPositiveNumber(this.minWidth());
    const minH = this.toPositiveNumber(this.minHeight());
    if (minW && width < minW) {
      return `Width ${Math.round(width)}px is below min ${minW}px`;
    }
    if (minH && height < minH) {
      return `Height ${Math.round(height)}px is below min ${minH}px`;
    }
    if (maxW && width > maxW) {
      return `Width ${Math.round(width)}px exceeds max ${maxW}px`;
    }
    if (maxH && height > maxH) {
      return `Height ${Math.round(height)}px exceeds max ${maxH}px`;
    }
    return null;
  }
  private validateVideoDuration(durationSeconds: number): string | null {
    if (this.type !== 'video') {
      return null;
    }
    if (!Number.isFinite(durationSeconds)) {
      return null;
    }
    const minDur = this.toPositiveNumber(this.minDuration());
    const maxDur = this.toPositiveNumber(this.maxDuration());
    if (minDur && durationSeconds < minDur) {
      return `Duration ${Math.round(durationSeconds)}s is below min ${minDur}s`;
    }
    if (maxDur && durationSeconds > maxDur) {
      return `Duration ${Math.round(durationSeconds)}s exceeds max ${maxDur}s`;
    }
    return null;
  }
  private async validateMediaFile(file: File): Promise<string | null> {
    const seq = ++this.mediaValidateSeq;
    try {
      if (this.type === 'image') {
        const meta = await this.readImageMeta(file);
        if (seq !== this.mediaValidateSeq) {
          return null;
        }
        return this.validateMediaDimensions(meta.width, meta.height);
      }
      if (this.type === 'video') {
        const meta = await this.readVideoMeta(file);
        if (seq !== this.mediaValidateSeq) {
          return null;
        }
        return this.validateMediaDimensions(meta.width, meta.height)
          || this.validateVideoDuration(meta.duration);
      }
      return null;
    } catch {
      if (seq !== this.mediaValidateSeq) {
        return null;
      }
      return `Unable to read ${this.type} metadata. Please choose a valid file.`;
    }
  }
  private async validateMediaUrl(url: string): Promise<string | null> {
    const seq = ++this.mediaValidateSeq;
    try {
      if (this.type === 'image') {
        const meta = await this.readImageMeta(url);
        if (seq !== this.mediaValidateSeq) {
          return null;
        }
        return this.validateMediaDimensions(meta.width, meta.height);
      }
      if (this.type === 'video') {
        const meta = await this.readVideoMeta(url);
        if (seq !== this.mediaValidateSeq) {
          return null;
        }
        return this.validateMediaDimensions(meta.width, meta.height)
          || this.validateVideoDuration(meta.duration);
      }
      return null;
    } catch {
      if (seq !== this.mediaValidateSeq) {
        return null;
      }
      // Remote/CORS failures shouldn't block browse selection when metadata is unreadable.
      return null;
    }
  }
  private readImageMeta(source: File | string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      let objectUrl = '';
      const cleanup = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = '';
        }
      };
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        cleanup();
        if (!width || !height) {
          reject(new Error('Invalid image dimensions'));
          return;
        }
        resolve({ width, height });
      };
      img.onerror = () => {
        cleanup();
        reject(new Error('Failed to load image'));
      };
      if (typeof source === 'string') {
        img.crossOrigin = 'anonymous';
        img.src = source;
      } else {
        objectUrl = URL.createObjectURL(source);
        img.src = objectUrl;
      }
    });
  }
  private readVideoMeta(source: File | string): Promise<{ width: number; height: number; duration: number }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      let objectUrl = '';
      const cleanup = () => {
        video.removeAttribute('src');
        video.load();
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = '';
        }
      };
      const onLoaded = () => {
        const width = video.videoWidth;
        const height = video.videoHeight;
        const duration = video.duration;
        cleanup();
        if (!width || !height || !Number.isFinite(duration)) {
          reject(new Error('Invalid video metadata'));
          return;
        }
        resolve({ width, height, duration });
      };
      video.onloadedmetadata = onLoaded;
      video.onerror = () => {
        cleanup();
        reject(new Error('Failed to load video'));
      };
      if (typeof source === 'string') {
        video.crossOrigin = 'anonymous';
        video.src = source;
      } else {
        objectUrl = URL.createObjectURL(source);
        video.src = objectUrl;
      }
    });
  }
  /** `png,jpg` / `.png,.jpg` / `image/*` → normalized comma list for accept= / matching. */
  private normalizeAcceptList(raw: string): string {
    return raw
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .map((part) => {
        const p = part.toLowerCase();
        if (p.includes('/') || p.startsWith('.')) {
          return p;
        }
        // Bare extension → `.ext`
        return `.${p.replace(/^\.+/, '')}`;
      })
      .join(',');
  }
  private fileMatchesAccept(file: File, accept: string): boolean {
    const parts = accept.split(',').map((p) => p.trim()).filter(Boolean);
    if (!parts.length) {
      return true;
    }
    const name = (file.name || '').toLowerCase();
    const mime = (file.type || '').toLowerCase();
    return parts.some((part) => {
      const p = part.toLowerCase();
      if (p === 'image/*') {
        return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name);
      }
      if (p === 'video/*') {
        return mime.startsWith('video/') || /\.(mp4|webm|ogg|mov)$/i.test(name);
      }
      if (p.endsWith('/*')) {
        return mime.startsWith(p.slice(0, -1));
      }
      if (p.startsWith('.')) {
        return name.endsWith(p);
      }
      return mime === p;
    });
  }
  private extensionMatchesAccept(pathOrUrl: string): boolean {
    const accept = this.resolvedAccept;
    if (!accept) {
      return true;
    }
    const parts = accept.split(',').map((p) => p.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) {
      return true;
    }
    const clean = pathOrUrl.split('?')[0].split('#')[0].toLowerCase();
    const hasExtParts = parts.filter((p) => p.startsWith('.'));
    if (!hasExtParts.length) {
      // Only mime wildcards — can't validate path extension strictly; allow.
      return true;
    }
    return hasExtParts.some((ext) => clean.endsWith(ext));
  }
  private setSingleValue(next: string): void {
    this.value = next;
    this.touched = true;
    this.revalidateCurrent();
    this.onChange(this.value);
    this.onValidatorChange();
    this.onTouched();
  }
  /**
   * A date or time field asks the control to validate again only when the errors it last read would
   * change. The field reports its state after every page write, and a page's `[min]` follows another
   * control's value: a run for nothing fires the control's valueChanges a second time, as on main
   * it did not, and re-runs the page's rules after its patch (an end before its start emptied).
   */
  private revalidateTime(): void {
    if (JSON.stringify(this.validateTime()) !== this.timeErrors) {
      this.onValidatorChange();
    }
  }
  private isTimeEmpty(): boolean {
    const value = this.timeValue;
    return value === null || value === undefined || (typeof value === 'string' && !value.trim());
  }
  /**
   * Date, time and datetime: half-typed text is invalid even though the value is `''`; `min`/`max`
   * are checked only with `valueAs="date"` (`sbInputMin` / `sbInputMax`, as Material's
   * `matDatepickerMin`/`Max` were); a text value must match its type, as before.
   */
  private validateTime(): ValidationErrors | null {
    if (this.timeHeld && !this.isTimeEmpty()) {
      return null;
    }
    if (this.timeTextInvalid) {
      return { sbInputInvalid: { value: this.timeText, type: this.type } };
    }
    const value = this.timeValue;
    if (this.isTimeEmpty()) {
      return this.required() ? { required: true } : null;
    }
    if (this.valueAs() === 'date' && this.type === 'date') {
      const day = boundDay(value as Date | string);
      if (!day) {
        return { sbInputInvalid: { value, type: this.type } };
      }
      const side = outOfBounds(day, boundDay(this.min()), boundDay(this.max()));
      if (side === 'min') {
        return { sbInputMin: { min: this.min(), actual: value } };
      }
      return side === 'max' ? { sbInputMax: { max: this.max(), actual: value } } : null;
    }
    return this.isValidValue(String(value).trim()) ? null : { sbInputInvalid: { value, type: this.type } };
  }
  /** The message a held value earns on its own: patched text that is not the type, or a `Date` out of bounds. */
  private timeValueError(): string {
    const value = this.timeValue;
    if (this.isTimeEmpty()) {
      return '';
    }
    if (this.valueAs() === 'date' && this.type === 'date') {
      const day = boundDay(value as Date | string);
      if (!day) {
        return invalidTextMessage('date', String(value));
      }
      const min = boundDay(this.min());
      const max = boundDay(this.max());
      const side = outOfBounds(day, min, max);
      const bound = side === 'min' ? min : max;
      return side && bound ? boundMessage(side, bound) : '';
    }
    const text = String(value).trim();
    return this.isValidValue(text) ? '' : invalidTextMessage(this.timeMode, text);
  }
  private emitChips(): void {
    this.value = this.chips.join(this.delimiter() || ',');
    this.touched = true;
    this.revalidateCurrent();
    this.onChange(this.value);
    this.onValidatorChange();
  }
  private splitValue(raw: string): string[] {
    if (!raw) {
      return [];
    }
    const delim = this.delimiter() || ',';
    return raw.split(delim).map((v) => v.trim()).filter(Boolean);
  }
  private revalidateCurrent(): void {
    if (this.error()) {
      this.internalError = '';
      return;
    }
    if (this.isTimeType) {
      // Date and time fields keep their messages in timeError / timeValueError.
      this.internalError = '';
      return;
    }
    const multiple = this.multiple();
    if (multiple && !this.supportsMultiple) {
      this.internalError = `Multiple is not supported for type "${this.type}"`;
      return;
    }
    if (multiple && !this.delimiter()) {
      this.internalError = 'Delimiter is required when multiple is enabled';
      return;
    }
    if (this.isMultiple) {
      if (!this.chips.length) {
        this.internalError = '';
        return;
      }
      const invalid = this.chips.find((chip) => !this.isValidValue(chip));
      this.internalError = invalid ? this.invalidMessage(invalid) : '';
      return;
    }
    const trimmed = String(this.value || '').trim();
    this.internalError = trimmed && !this.isValidValue(trimmed) ? this.invalidMessage(trimmed) : '';
  }
  private clearLocalPreview(): void {
    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl);
      this.localPreviewUrl = '';
    }
  }
  private extractVariableText(variable: unknown): string {
    if (variable == null) {
      return '';
    }
    if (typeof variable === 'string') {
      return variable;
    }
    const obj = variable as {
      key?: string;
      code?: string;
      value?: string;
      variable?: string;
      name?: string;
    };
    return obj.key || obj.code || obj.value || obj.variable || '';
  }
}
